import * as React from "react";
import { useEffect, useState } from "react";
import { Button } from "@mui/material";
import { styled } from "@mui/material/styles";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";
import { useDoorConfigurationsApi } from "../../v2/api/door_configurations_api";
import { Allegati } from "../../v2/models/allegati";
import Loading from "../Loading/loading";

interface AllegatiUploaderProps {
  currentUser: any;
  quote_id: any;
}

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

export const AllegatiUploader: React.FC<AllegatiUploaderProps> = ({
  currentUser,
  quote_id,
}) => {
  const [selectedFiles, setSelectedFiles] = useState<Allegati[]>([]);
  const [loading, setLoading] = useState(false);
  const doorConfigurationsApi = useDoorConfigurationsApi();

  // 🔹 Fetch iniziale dei file
  useEffect(() => {
    const fetchFiles = async () => {
      if (!quote_id) return;
      setLoading(true);
      try {
        const files: Allegati[] = await doorConfigurationsApi.getAllegati(
          currentUser,
          quote_id
        );
        setSelectedFiles(files); // files è già un array
      } finally {
        setLoading(false);
      }
    };

    fetchFiles();
  }, [quote_id]);

  // 🔹 Upload file
  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!quote_id || !event.target.files) return;

    const newFiles = Array.from(event.target.files);
    setLoading(true);
    try {
      await Promise.all(
        newFiles.map((file) =>
          doorConfigurationsApi.saveAllegati(
            currentUser,
            quote_id,
            file
          )
        )
      );

      // Ricarica l’elenco aggiornato
      const files: Allegati[] = await doorConfigurationsApi.getAllegati(
        currentUser,
        quote_id
      );
      setSelectedFiles(files);
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Rimozione file
  const handleRemoveFile = async (index: number) => {
    if (!quote_id) return;
    setLoading(true);
    try {
      await doorConfigurationsApi.deleteAllegato(
        currentUser,
        quote_id,
        selectedFiles[index].guid
      );
      setSelectedFiles((prevFiles) =>
        prevFiles.filter((_, i) => i !== index)
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>

      <Button
        component="label"
        variant="contained"
        startIcon={<CloudUploadIcon />}
        disabled={loading}
      >
        Carica file
        <VisuallyHiddenInput
          type="file"
          multiple
          onChange={handleFileChange}
        />
      </Button>

      {selectedFiles.length > 0 && (
        <div className="mt-2">
          {selectedFiles.map((file, index) => (
            <div
              key={file.guid}
              className="flex items-center justify-between p-2 border rounded-md mb-1"
            >
              <span>{file.filename}</span>
              <DeleteForeverRoundedIcon
                className="text-red-500 cursor-pointer hover:text-red-700"
                onClick={() => handleRemoveFile(index)}
              />
            </div>
          ))}
        </div>
      )}
      {loading && <Loading />}
    </div>
  );
};
